import { ChangeEvent, FormEvent, useMemo, useState } from "react";

import { CreateEmployerJob } from "types/employer.interface";
import useServiceCategories from "hooks/userServiceCategories";
import { GENDER, MyndaType, yearsOfExperience } from "shared";
import useUsers from "hooks/useUsers";

interface Props {
  onNext: () => void;
  onBack: () => void;
  input: CreateEmployerJob;
  onChange: (
    e:
      | ChangeEvent<HTMLInputElement>
      | ChangeEvent<HTMLSelectElement>
      | ChangeEvent<HTMLTextAreaElement>
      | {
          target: {
            name: string;
            value: string | number;
          };
        }
  ) => void;
  onChangeByNameValue: (name: keyof CreateEmployerJob, value: any) => void;
}

export default function MyndaPreference({
  onNext,
  input,
  onChange,
  onChangeByNameValue,
  onBack,
}: Props) {
  const { data: serviceCategories } = useServiceCategories();
  const [showTerms, setShowTerms] = useState(false);
  const { data: users, isLoading } = useUsers({
    search: "Employer",
    page: 1,
    limit: 500,
  });

  const submitHandler = async (e: FormEvent) => {
    e.preventDefault();

    if (["Part-Time", "Full-Time"].includes(input.employmentType!)) {
      onChangeByNameValue("jobType", MyndaType.LeaveIn);
      onNext();
    } else {
      onChangeByNameValue("jobType", MyndaType.LeaveOut);
      onNext();
    }
  };

  const categories = useMemo(() => {
    return serviceCategories?.data.map((cat) => ({
      label: cat.name,
      value: cat.name,
    }));
  }, [serviceCategories?.data]);

  const toggleTerms = () => {
    setShowTerms(!showTerms);
  };

  const handleNext = () => {
    onNext();
  };
  return (
    <div className="w-full ">
      <form onSubmit={submitHandler} className=" flex flex-col gap-8 ">
        <div className="flex flex-col gap-1">
          <h2 className=" font-body font-medium text-lg lg:text-xl leading-5 text-body">
            Help us understand your ideal Mynda
          </h2>
        </div>

        <div className=" mt-4 flex flex-col gap-2 ">
          <label htmlFor="user">Select Employer</label>
          <select
            id="user"
            name="user"
            value={input.user}
            // @ts-ignore
            defaultValue={input.owner?._id}
            required
            onChange={onChange}
            className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">{isLoading ? "Loading.." : "Select..."}</option>
            {users?.data?.data?.map((item) => {
              return (
                <option key={item._id} value={item._id}>
                  {item.firstName} {item.lastName}
                </option>
              );
            })}
          </select>
        </div>

        <div className="flex flex-col gap-2 w-full">
          <label htmlFor="occupation">Type of Mynda</label>
          <select
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            name="category"
            onChange={onChange}
            value={input.category}
            defaultValue={input.category}
            required
          >
            <option value="">Select Service Category</option>
            {categories?.map((category) => (
              <option key={category.label} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="yoe">Years of Experience</label>

          <select
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            name="yoe"
            onChange={onChange}
            value={input.yoe}
            defaultValue={input.yoe}
            required
          >
            <option value="">Select Service Category</option>
            {yearsOfExperience?.map((category) => (
              <option key={category.label} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2 w-full">
          <label htmlFor="gender">Preferred Gender </label>
          <select
            className="w-full px-3 py-2 text-sm rounded-md border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent placeholder:text-gray-400"
            name="gender"
            onChange={onChange}
            value={input.gender}
            defaultValue={input.gender}
            required
          >
            <option value="">Select Service Category</option>
            {GENDER?.map((category) => (
              <option key={category.label} value={category.value}>
                {category.label}
              </option>
            ))}
          </select>
        </div>

        <div className="w-full ">
          <label htmlFor="jobType" className=" mb-3">
            Let us know what type of care you need and when you need it ?
          </label>
          <div className=" mt-3 grid  grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div
              onClick={() => onChangeByNameValue("jobType", MyndaType.LeaveIn)}
              className={`p-4 border rounded-sm cursor-pointer ${
                ["Part-Time", "Full-Time"].includes(input.employmentType!)
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-300"
              }`}
            >
              <div className="text-blue-600 font-semibold">🏠 Live-in care</div>
              <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-4">
                <label className="flex items-center space-x-1">
                  <input
                    type="radio"
                    checked={input.employmentType === "Part-Time"}
                    onChange={() =>
                      onChangeByNameValue("employmentType", "Part-Time")
                    }
                    className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span>Part time</span>
                </label>
                <label className="flex items-center space-x-1">
                  <input
                    type="radio"
                    checked={input.employmentType === "Full-Time"}
                    onChange={() =>
                      onChangeByNameValue("employmentType", "Full-Time")
                    }
                    className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span>Full time</span>
                </label>
              </div>
            </div>

            <div
              onClick={() => onChangeByNameValue("jobType", MyndaType.LeaveOut)}
              className={`p-4 border rounded-sm cursor-pointer ${
                ["Daily", "Hourly"].includes(input.employmentType!)
                  ? "border-blue-600 bg-blue-50"
                  : "border-gray-300"
              }`}
            >
              <div className="text-blue-600 font-semibold">
                ⏳{MyndaType.LeaveOut}
              </div>
              <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center space-y-1 sm:space-y-0 sm:space-x-4">
                <label className="flex items-center space-x-1">
                  <input
                    type="radio"
                    checked={input.employmentType === "Hourly"}
                    onChange={() =>
                      onChangeByNameValue("employmentType", "Hourly")
                    }
                    className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span>Hourly</span>
                </label>
                <label className="flex items-center space-x-1">
                  <input
                    type="radio"
                    checked={input.employmentType === "Daily"}
                    onChange={() =>
                      onChangeByNameValue("employmentType", "Daily")
                    }
                  />
                  <span>Daily</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {input.employmentType === "Part-Time" && (
          <div className="flex flex-col gap-2">
            <label htmlFor="partTimeDays">What is the number of days?</label>
            <input
              placeholder=""
              id="partTimeDays"
              name="partTimeDays"
              value={input.partTimeDays!}
              onChange={onChange}
              required
              type="number"
              inputMode="numeric"
              className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        {["Daily", "Hourly"].includes(input.employmentType!) && (
          <div className="flex flex-col gap-2">
            <label htmlFor="liveOutNumber">How many per month?</label>
            <input
              placeholder=""
              id="liveOutNumber"
              name="liveOutNumber"
              value={input.liveOutNumber!}
              onChange={onChange}
              required
              type="number"
              inputMode="numeric"
              className="border bg-input border-gray-300 rounded-sm px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        )}

        <div className="flex items-center gap-4">
          <button className="px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 transition-colors">
            Next
          </button>
        </div>
      </form>
    </div>
  );
}
